import clsx from "clsx";
import { ArrowDown, ArrowUp, Search, X } from "lucide-react";
import { useDeferredValue, useMemo, useState, type ReactNode } from "react";
import { isNumeric, toNumber } from "@/lib/csv";

const ROW_HEIGHT = 28;
const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

/** A table of text rows: sort, filter, and only the rows on screen are in the page, so large tables stay fast. */
export default function DataTable({ names, body, emptyText, children }: { names: string[]; body: string[][]; emptyText: string; children?: ReactNode }) {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<{ col: number; desc: boolean } | null>(null);
  const [cell, setCell] = useState<{ row: string[]; col: number } | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const needle = useDeferredValue(query.trim().toLowerCase());

  const columns = useMemo(() => {
    const count = body.reduce((n, r) => Math.max(n, r.length), names.length);
    return Array.from({ length: count }, (_, i) => {
      const name = names[i]?.trim() || `Column ${i + 1}`;
      const values = body.map((r) => r[i]?.trim() ?? "").filter(Boolean);
      const longest = values.slice(0, 200).reduce((n, v) => Math.max(n, v.length), name.length + 2);
      return { name, numeric: values.length > 0 && values.every(isNumeric), width: Math.min(320, Math.max(72, longest * 7.5 + 20)) };
    });
  }, [names, body]);

  const rows = useMemo(() => {
    const found = needle ? body.filter((r) => r.some((c) => c.toLowerCase().includes(needle))) : body;
    if (!sort) return found;
    const numeric = columns[sort.col]?.numeric;
    const empty = (r: string[]) => !r[sort.col]?.trim();
    const compare = (a: string[], b: string[]) => (numeric ? toNumber(a[sort.col]) - toNumber(b[sort.col]) : collator.compare(a[sort.col], b[sort.col]));
    return [...found].sort((a, b) => {
      if (empty(a) || empty(b)) return Number(empty(a)) - Number(empty(b)); // empty cells last, either way
      return sort.desc ? compare(b, a) : compare(a, b);
    });
  }, [body, needle, sort, columns]);

  const onScreen = Math.ceil(window.innerHeight / ROW_HEIGHT);
  const first = Math.max(0, Math.min(Math.floor(scrollTop / ROW_HEIGHT), rows.length - onScreen) - 10); // a filter can leave scrollTop past the end
  const last = Math.min(rows.length, first + onScreen + 20);
  const numberWidth = String(body.length).length * 8 + 20;
  const clickSort = (col: number) => setSort(sort?.col !== col ? { col, desc: false } : sort.desc ? null : { col, desc: true });

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-zinc-200 px-3 py-1.5 text-xs text-zinc-500 dark:border-zinc-800">
        <label className="flex min-w-32 flex-1 items-center gap-1.5">
          <Search size={14} className="shrink-0" />
          <input className="w-full bg-transparent text-sm text-zinc-900 outline-none dark:text-zinc-100" placeholder="Filter rows" value={query} onChange={(e) => setQuery(e.target.value)} />
        </label>
        <span>
          {needle ? `${rows.length.toLocaleString()} of ${body.length.toLocaleString()}` : body.length.toLocaleString()} {body.length === 1 ? "row" : "rows"}, {columns.length} {columns.length === 1 ? "column" : "columns"}
        </span>
        {children}
      </div>
      {columns.length === 0 ? (
        <p className="p-6 text-center text-sm text-zinc-500">{emptyText}</p>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto" onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}>
          <table className="table-fixed border-separate border-spacing-0 text-xs" style={{ width: numberWidth + columns.reduce((n, c) => n + c.width, 0) }}>
            <colgroup>
              <col style={{ width: numberWidth }} />
              {columns.map((c, i) => (
                <col key={i} style={{ width: c.width }} />
              ))}
            </colgroup>
            <thead className="sticky top-0 z-20">
              <tr style={{ height: ROW_HEIGHT }}>
                <th className="sticky left-0 z-10 border-r border-b border-zinc-200 bg-zinc-100 dark:border-zinc-800 dark:bg-zinc-900" />
                {columns.map((c, i) => (
                  <th key={i} aria-sort={sort?.col === i ? (sort.desc ? "descending" : "ascending") : undefined} className="border-r border-b border-zinc-200 bg-zinc-100 p-0 font-medium dark:border-zinc-800 dark:bg-zinc-900">
                    <button className={clsx("flex h-full w-full items-center gap-1 px-2 hover:bg-zinc-200 dark:hover:bg-zinc-800", c.numeric && "justify-end")} title={c.name} onClick={() => clickSort(i)}>
                      <span className="truncate">{c.name}</span>
                      {sort?.col === i && (sort.desc ? <ArrowDown size={12} className="shrink-0" /> : <ArrowUp size={12} className="shrink-0" />)}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {first > 0 && <tr style={{ height: first * ROW_HEIGHT }} />}
              {rows.slice(first, last).map((r, i) => (
                <tr key={first + i} style={{ height: ROW_HEIGHT }}>
                  <td className="sticky left-0 z-10 border-r border-b border-zinc-200 bg-zinc-50 px-1 text-right text-zinc-400 tabular-nums dark:border-zinc-800 dark:bg-zinc-900">{first + i + 1}</td>
                  {columns.map((c, col) => (
                    <td
                      key={col}
                      className={clsx(
                        "cursor-default truncate border-r border-b border-zinc-100 px-2 dark:border-zinc-800/60",
                        c.numeric && "text-right tabular-nums",
                        cell?.row === r && cell.col === col && "bg-indigo-100 dark:bg-indigo-900/50",
                      )}
                      onClick={() => setCell({ row: r, col })}
                    >
                      {r[col]}
                    </td>
                  ))}
                </tr>
              ))}
              {last < rows.length && <tr style={{ height: (rows.length - last) * ROW_HEIGHT }} />}
            </tbody>
          </table>
          {rows.length === 0 && <p className="sticky left-0 p-6 text-center text-sm text-zinc-500">{body.length ? "No rows match." : emptyText}</p>}
        </div>
      )}
      {cell && (
        <div className="flex max-h-40 shrink-0 gap-2 overflow-auto border-t border-zinc-200 p-3 text-sm dark:border-zinc-800">
          <div className="min-w-0 flex-1">
            <div className="text-xs text-zinc-500">{columns[cell.col]?.name}</div>
            <div className="break-words whitespace-pre-wrap select-text">{cell.row[cell.col] ?? ""}</div>
          </div>
          <button className="h-fit rounded p-1 text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800" aria-label="Close" onClick={() => setCell(null)}>
            <X size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
